<template>
  <div style="width: 100%;">
    <slot />
    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title">{{ $tc("Workspace", 2) }}</span>
        <v-spacer />
        <v-text-field
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          :label="$t('Search')"
          :placeholder="$t('Search')"
          class="data-table-search"
          single-line
          hide-details
        />
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="workspacesFilter"
        :loading="loading"
        loading-text=""
        :page.sync="pagination.page"
        :items-per-page.sync="paginationWorkSpaceDataTable.rowsPerPage"
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :class="{ 'no-show-permission': !userHasPermissionTo('show') }"
        :header-props="{ sortIcon }"
        :footer-props="{ prevIcon, nextIcon,'items-per-page-options': rowsPerPageItems }"
        @pagination="setRowsOnLocalStorage(dataTableWorkspaceDataTableId,paginationWorkSpaceDataTable.rowsPerPage)"
      >
        <template v-slot:item="props">
          <tr
            :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
            @click="viewItem(props.item)"
          >
            <td>{{ props.item.wp_label }}</td>
            <td>{{ props.item.wp_plan_name ? props.item.wp_plan_name : "-"}}</td>
            <td>{{ props.item.wp_updated_at }}</td>

            <td>{{ props.item.wp_created_at }}</td>

            <td>
              <div
                class="user-status-icon"
                :class="{ 'status-inactive': !props.item.wp_status_active }"
              />
              <span>
                {{
                  props.item.wp_status_active
                    ? $tc("OpenNoun", 1)
                    : $tc("ClosedNoun", 1)
                }}
              </span>
            </td>

            <td>
              <div
                class="user-status-icon"
                :class="{ 'status-inactive': !props.item.wp_access_active }"
              />
              <span>
                {{
                  props.item.wp_access_active ? $t("Visible") : $t("NotVisible")
                }}
              </span>
            </td>

            <td>{{ getProjectPhaseName(props.item.wp_status) }}</td>

            <td v-if="showActionButtons">
              <app-datatable-action-buttons
                :user-can-open="userHasModulePermission('module_design')"
                @open="openItem(props.item)"
                :user-can-edit="userHasPermissionTo('update')"
                @edit="editItem(props.item)"
                :user-can-delete="userHasPermissionTo('destroy')"
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
          </tr>
        </template>

        <template v-slot:pageText="props">
          {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />

          <v-alert
            v-else
            class="data-table-no-data justify-center"
            icon="warning"
          >
            {{ $t("NoDataWorkspace") }}
          </v-alert>
        </template>
      </v-data-table>
    </div>

    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteWorkspace')"
      :content="$t('DeleteConfirmation') + ' ' + itemToDelete.wp_label + '?'"
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="confirmDelete"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import DataTableNoResults from "../../_Atoms/DataTableNoResults";
import AppDialogBox from "../../_Molecules/AppDialogBox";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import { listsMixin } from "@/mixins/listsMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";

export default {
  name: "WorkspaceDataTable",

  components: {
    AppDatatableActionButtons,
    AppDialogBox,
    DataTableNoResults
  },

  mixins: [managementDatatableMixin, listsMixin, checkPermissionsMixin],

  data() {
    return {
      data: null,
      editIndex: 0,
      dataTableWorkspaceDataTableId: "dataTableWorkspaceDataTableId" ,
      paginationWorkSpaceDataTable: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableWorkspaceDataTableId")
      },
      workspaces: [],
      workspacesFilter: [],
      projectPhases: [],
      loadingDelete: false,
      permissionName: "workspace"
    };
  },

  computed: {
    mainHeaders() {
      return [
        {
          text: this.$i18n.t("Name"),
          align: "left",
          value: "wp_label",
          width: "20%"
        },
        {
          text: this.$i18n.t("PlanName"),
          align: "left",
          value: "wp_plan_name",
          width: "10%"
        },
        {
          text: this.$i18n.t("LastChange"),
          align: "left",
          value: "wp_updated_at",
          width: "10%"
        },
        {
          text: this.$i18n.t("CreationDate"),
          align: "left",
          value: "wp_created_at",
          width: "10%"
        },
        {
          text: this.$i18n.t("Status"),
          align: "left",
          value: "wp_status_active",
          width: "10%"
        },
        {
          text: this.$i18n.t("Access"),
          align: "left",
          value: "wp_access_active",
          width: "10%"
        },
        {
          text: this.$i18n.t("Stage"),
          align: "left",
          value: "wp_status",
          width: "20%"
        },
      ];
    },

    showActionButtons(){
      return this.userHasModulePermission('module_design') || this.userHasPermissionTo('update') || this.userHasPermissionTo('destroy');
    }
  },

  watch: {
    searchFromParent(value) {
      this.workspacesFilter = this.workspaces.filter(workspace =>
        workspace.wp_label.toLowerCase().includes(value.toLowerCase())
      );
    }
  },

  mounted() {
    this.getWorkspaces();
    this.getProjectPhases();
  },

  methods: {
    ...mapActions(["fetchAllWorkspaces", "DELETE_WORKSPACE", "GET_LIST"]),

    async getWorkspaces() {
      try {
        this.loading = true;
        this.workspaces = await this.fetchAllWorkspaces({
          includes: null,
          fields:
            "wp_uuid,wp_label,wp_id,wp_updated_at,wp_created_at,wp_status_active,wp_access_active,wp_status,wp_plan_name"
        });
        this.workspacesFilter = this.workspaces;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    getProjectPhases() {
      this.GET_LIST({ listName: "list_statut", code: [], includeDeleted: true })
        .then(projectPhases => {
          this.projectPhases = projectPhases;
        })
        .catch(error => {
          console.error(error);
        });
    },

    getProjectPhaseName(id) {
      if (id && this.projectPhases.length > 0) {
        let phase = this.projectPhases.find(item => item.code === id);
        return this.getListElementLabel(phase);
      } else return id;
    },

    viewItem(item) {
      this.$router.push({ name: "workspace", params: { uuid: item.wp_uuid } });
    },

    openItem(item){
      this.$router.push({
        name: "home",
        params: { workspace_uuid: item.wp_uuid }
      });
    },

    editItem(item) {
      this.$router.push({
        name: "workspace-edit",
        params: { uuid: item.wp_uuid }
      });
    },

    async confirmDelete() {
      try {
        let workspace = this.itemToDelete;
        this.loadingDelete = true;
        await this.DELETE_WORKSPACE(workspace.wp_id);
        this.getWorkspaces();
        this.deleteDialog = false;
        this.itemToDelete = {};
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingDelete = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/ProjectManagement/Workspaces/WorkspaceDataTable.vue